import React, { useMemo } from "react";
// Consumer Drawer Content

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../redux/hooks";
import {
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Popover,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import { ArrowBackIos } from "@mui/icons-material";
import ConsumerConsumptionTrends from "./ConsumerConsumptionTrends";
import ConsumerDemandForecasting from "./ConsumerDemandForecasting";
import PaymentHistory from "./PaymentHistory";
import ConsumerDetectionAlert from "./ConsumerDetectionAlert";
import AssociatedMeterLogs from "./AssociatedMeterLogs";
import CustomDataTable from "../table/CustomTable";
import ReadClusterLineChart from "../charts/ReadClusterLineChart";
import moment from "moment";
import "moment-timezone";
import {
  formatDate,
  formatMeterSerialNumber,
  formatTime,
} from "../../utils/helper";
import Calendar2 from "../../assets/svg/calendar2";
import ContainedButton from "../../forms/button/ContainedButton";
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import CustomInputLabelSelect from "../../forms/select/InputLabelCustomSelect";
import DownArrow from "../../assets/svg/downArrow";
import useReadCluster from "../../hooks/useReadCluster";
import CustomToast from "../toast/CustomToast";
import { fetchGETRequest } from "../../utils/services";
import LottieLoader from "../animatedLoader/LottieLoader";
import { findValue, transformV2Record } from "../../utils/meterDataMapper";
import { LiveDataRecord, ToastState } from "../../types";
import { UnifiedIcon } from "../Icon/UnifiedIcon";
import { Edit, Edit2 } from "lucide-react";
import UpdateReadValueModel from "../modals/UpdateReadValue";
import CreateReadValueModel from "../modals/CreateReadValue";
import ConsumerDrawerBillAndPayment from "./ConsumerDrawerBill&Payment";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import Download from "../../assets/svg/download";
import { ConsumerWorkOrderHistory } from "./ConsumerWorkOrderHistory";
import { ConsumerBillingDeterminants } from "./ConsumerBillingDeterminants";
import MeterAlerts from "./MeterAlerts";
import MeterEvents from "./MeterEvents";
import MockMeterEvents from "./MockMeterEvents";

interface MeterData {
  meterId: string;
  serialNumber: string;
  meterNumber: string;
  meterType: string;
  meterStatus: string;
  id: string;
  status: string | null;
  timeseries: {
    date: string;
    value: string;
  };
}

interface ConsumerMeterDrawerContentProps {
  onClose: () => void;
  data: MeterData;
  onRefresh?: () => void;
}

interface RegisterType {
  type: string;
  value: string;
}

const ConsumerMeterDrawerContent: React.FC<ConsumerMeterDrawerContentProps> = ({
  onClose,
  data,
  onRefresh,
}) => {
  const [value, setValue] = useState<string>("TLR");
  const { environment } = useAppSelector((state) => state.auth);

  // console.log(data);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Stack
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        gap={1}
        p={2}
      >
        <IconButton
          sx={{
            p: 0.5,
          }}
          onClick={onClose}
        >
          <ArrowBackIos style={{ fontSize: 18, color: "black" }} />
        </IconButton>
        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          flex={1}
        >
          <Stack flexDirection={"row"} gap={1}>
            <Typography
              variant="inherit"
              fontSize={"18px"}
              fontWeight={700}
              color="#1064ED"
            >
              Meter Name: {formatMeterSerialNumber(data?.meterNumber)}
            </Typography>
          </Stack>
          {/* <Button
            sx={{
              textTransform: "none",
              backgroundColor: "red",
              color: "white",
              borderRadius: 2,
            }}
          >
            Meter Disconnect
          </Button> */}
        </Stack>
      </Stack>

      <Box sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList
              onChange={handleChange}
              aria-label="lab API tabs example"
              sx={{ height: 30, minHeight: 30 }}
              scrollButtons="auto"
              allowScrollButtonsMobile
              variant="scrollable"
            >
              <Tab label="Telemetry Readings" value="TLR" />
              {environment !== "Test" && (
                <Tab label="Consumption Analysis & Forecast" value="CAS" />
              )}
              <Tab label="Wallet Transactions" value="2" />
              <Tab label="Meter Events" value="3" />
              <Tab label="Work Order History" value="4" />
              {/* <Tab label="Billing Determinants" value="5" /> */}
            </TabList>
          </Box>
          {/* Keep all tabs mounted to preserve state and prevent API refetches */}
          <Box sx={{ display: value === "TLR" ? "block" : "none", p: 0 }}>
            <RenderTlr id={data?.meterNumber || data?.id} />
          </Box>
          {environment !== "Test" && (
            <Box sx={{ display: value === "CAS" ? "block" : "none", p: 0 }}>
              <ConsumerConsumptionTrends />
              <ConsumerDemandForecasting />
            </Box>
          )}
          <Box sx={{ display: value === "2" ? "block" : "none", p: 0 }}>
            {/* <PaymentHistory setDisputesOpen={() => {}} /> */}
            <ConsumerDrawerBillAndPayment
              meterId={data?.id}
              onRefresh={onRefresh}
            />
          </Box>
          <Box sx={{ display: value === "3" ? "block" : "none", p: 0 }}>
            {environment === "Test" ? (
              <MeterAlerts meterSerialNumber={data?.meterNumber} />
            ) : (
              <MockMeterEvents />
            )}
            {/* <MeterEvents /> */}
            {/* <ConsumerDetectionAlert /> */}
            {/* <AssociatedMeterLogs /> */}
          </Box>
          <Box sx={{ display: value === "4" ? "block" : "none", p: 0 }}>
            <ConsumerWorkOrderHistory meterId={data?.id} activeTab={value} />
          </Box>
          <Box sx={{ display: value === "5" ? "block" : "none", p: 0 }}>
            <ConsumerBillingDeterminants meterId={data?.id} />
          </Box>
        </TabContext>
      </Box>
    </Box>
  );
};

export default ConsumerMeterDrawerContent;

const convertToTlrData = (response: any, registerTypeValue: RegisterType) => {
  const filteredData = response[registerTypeValue?.value] || [];

  const transformedLiveData = filteredData.map((record: any) => ({
    currentDateTime: `${moment.utc(record.ts).format("YYYY-MM-DDTHH:mm:ss")}Z`,
    currentRead: record.value,
    currentReadQuality: "Good",
    readId: registerTypeValue?.value,
  }));

  const sortedData = filteredData
    .sort(
      (a: any, b: any) => new Date(a.ts).getTime() - new Date(b.ts).getTime(),
    )
    .map((record: any) => ({
      currentDateTime: moment.utc(record.ts).format("YYYY-MM-DD HH:mm:ss"),
      currentRead: record.value,
      currentReadQuality: "Good",
    }));

  const chartData = {
    xAxis: sortedData.map((item: any) => item.currentDateTime),
    yAxis: sortedData.map((item: any) => item.currentRead),
    legend: registerTypeValue?.value,
  };

  return { transformedLiveData, chartData };
};

const CHIP_COLORS = {
  good: "#7FCA59",
  pending: "#FDA81F",
  failed: "#F65B5B",
  high: "#F65B5B",
  medium: "#FDA81F",
  low: "#7FCA59",
  info: "#007AFF",
  closed: "#7FCA59",
  open: "#F65B5B",
  investigation: "#FDA81F",
};

const getTimeStamp = (newTime: string, newDate: Date): number => {
  const [hours, minutes] = newTime.split(":").map(Number);

  // Use moment-timezone to create a moment in IST
  const istMoment = moment.tz(newDate, "Asia/Kolkata").set({
    hour: hours,
    minute: minutes,
    second: 0,
    millisecond: 0,
  });

  return istMoment.valueOf(); // Returns UTC timestamp representing the selection in IST
};

const PROFILE_TYPES = [
  { type: "Instant read", value: "Instant" },
  { type: "Block Read", value: "Load Profile" },
  { type: "Daily read", value: "Daily" },
  { type: "Billing read", value: "Billing" },
];

const PROFILE_OPTIONS: Record<string, RegisterType[]> = {
  Instant: [
    { type: "Voltage", value: "voltage" },
    // { type: "Voltage L1", value: "voltageL1" },
    // { type: "Voltage L2", value: "voltageL2" },
    // { type: "Voltage L3", value: "voltageL3" },
    { type: "Current", value: "phaseCurrent" },
    // { type: "Current L1", value: "currentL1" },
    // { type: "Current L2", value: "currentL2" },
    // { type: "Current L3", value: "currentL3" },
    { type: "Neutral Current", value: "neutralCurrent" },
    { type: "Power Factor", value: "powerFactor" },
    { type: "Frequency", value: "frequency" },
    { type: "Apparent Power (VA)", value: "apparentPowerVa" },
    { type: "Active Power (W)", value: "activePowerW" },
    { type: "Reactive Power (VAR)", value: "reactivePowerVar" },
    { type: "kWh Import", value: "cumulativeEnergyKwhImport" },
    { type: "kVah Import", value: "cumulativeEnergyKvahImport" },
    { type: "kWh Export", value: "cumulativeEnergyKwhExport" },
    { type: "kVah Export", value: "cumulativeEnergyKvahExport" },
    { type: "MD (kW)", value: "maxDemandKw" },
    { type: "MD (kVA)", value: "maxDemandKva" },
    { type: "Tamper Count", value: "tamperCount" },
    { type: "Billing Count", value: "billingCount" },
    { type: "Programming Count", value: "programmingCount" },
    { type: "Relay Status", value: "relayStatus" },
  ],
  Daily: [
    { type: "kWh Import", value: "cumulativeEnergyKwhImport" },
    { type: "kVah Import", value: "cumulativeEnergyKvahImport" },
    { type: "kWh Export", value: "cumulativeEnergyKwhExport" },
    { type: "kVah Export", value: "cumulativeEnergyKvahExport" },
    { type: "Voltage", value: "voltage" },
    { type: "Phase Current", value: "phaseCurrent" },
    { type: "Neutral Current", value: "neutralCurrent" },
    { type: "Power Factor", value: "powerFactor" },
    { type: "Frequency", value: "frequency" },
    { type: "Apparent Power (VA)", value: "apparentPowerVa" },
    { type: "Active Power (W)", value: "activePowerW" },
    { type: "Max Demand (kW)", value: "maxDemandKw" },
    { type: "Max Demand (kVA)", value: "maxDemandKva" },
    { type: "Max Demand kW Time", value: "maxDemandKwDatetime" },
    { type: "Max Demand kVA Time", value: "maxDemandKvaDatetime" },
  ],
  "Load Profile": [
    { type: "Avg Voltage", value: "avgVoltage" },
    { type: "Avg Current", value: "avgCurrent" },
    { type: "kWh Import", value: "blockEnergyKwhImport" },
    { type: "kVah Import", value: "blockEnergyKvahImport" },
    { type: "kWh Export", value: "blockEnergyKwhExport" },
    { type: "kVah Export", value: "blockEnergyKvahExport" },
  ],
  Billing: [
    { type: "Avg Power Factor", value: "avgPowerFactor" },
    { type: "Cumulative Energy (kWh)", value: "cumEnergyKwh" },
    { type: "Cumulative Energy (kVah)", value: "cumEnergyKvah" },
    { type: "Max Demand (kW)", value: "maxDemandKw" },
    { type: "Max Demand (kVA)", value: "maxDemandKva" },
    { type: "Max Demand kW Time", value: "maxDemandKwDatetime" },
    { type: "Max Demand kVA Time", value: "maxDemandKvaDatetime" },
    // { type: "Energy kWh Tz1", value: "cumEnergyKwhTz1" },
    // { type: "Energy kWh Tz2", value: "cumEnergyKwhTz2" },
    // { type: "Energy kWh Tz3", value: "cumEnergyKwhTz3" },
    // { type: "Energy kWh Tz4", value: "cumEnergyKwhTz4" },
    // { type: "Energy kWh Tz5", value: "cumEnergyKwhTz5" },
    // { type: "Energy kWh Tz6", value: "cumEnergyKwhTz6" },
    // { type: "Energy kWh Tz7", value: "cumEnergyKwhTz7" },
    // { type: "Energy kWh Tz8", value: "cumEnergyKwhTz8" },
    // { type: "Energy kVAh Tz1", value: "cumEnergyKvahTz1" },
    // { type: "Energy kVAh Tz2", value: "cumEnergyKvahTz2" },
    // { type: "Energy kVAh Tz3", value: "cumEnergyKvahTz3" },
    // { type: "Energy kVAh Tz4", value: "cumEnergyKvahTz4" },
    // { type: "Energy kVAh Tz5", value: "cumEnergyKvahTz5" },
    // { type: "Energy kVAh Tz6", value: "cumEnergyKvahTz6" },
    // { type: "Energy kVAh Tz7", value: "cumEnergyKvahTz7" },
    // { type: "Energy kVAh Tz8", value: "cumEnergyKvahTz8" },
  ],
};

const startOfDayIST = moment().tz("Asia/Kolkata").startOf("day").valueOf();
const endOfDayIST = moment().tz("Asia/Kolkata").endOf("day").valueOf();

interface RenderTlrProps {
  id?: string;
}

const RenderTlr: React.FC<RenderTlrProps> = ({ id }) => {
  const meterId = id;

  const [profileType, setProfileType] = useState<string>("Instant");
  const [registerType, setRegisterType] = useState<RegisterType>(
    PROFILE_OPTIONS["Instant"][0],
  );

  const COLUMNS = ["Read Date/Time", registerType.type, "VEE Status", "Action"];
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [toast, setToast] = useState<ToastState>({ message: "", severity: "" });
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);
  const [timeStamp, setTimeStamp] = useState({
    start: startOfDayIST,
    end: endOfDayIST,
  });
  const [data, setData] = useState<LiveDataRecord[]>([]);
  const [chartData, setChartData] = useState<{
    xAxis: string[];
    yAxis: (string | number)[];
    legend: string;
  }>({
    xAxis: [],
    yAxis: [],
    legend: "",
  });

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const [downloadEl, setDownloadEl] = useState<HTMLElement | null>(null);
  const [csvLoader, setCsvLoader] = useState<boolean>(false);
  const [excelLoader, setExcelLoader] = useState<boolean>(false);

  const [updateReadValueModal, setUpdateReadValueModal] =
    useState<LiveDataRecord | null>(null);

  const [createReadValueModal, setCreateReadValueModal] =
    useState<boolean>(false);

  // const { chartData, transformedLiveData: data } = convertToTlrData(
  //   DummyReadClusterData,
  //   registerType
  // );

  // Fetch a large amount of data from API, paginate client-side
  const API_FETCH_LIMIT = 10000;

  const {
    data: apiData,
    refetch,
    isLoading,
    error,
  } = useReadCluster({
    meterId: meterId || "",
    registerType,
    timeStamp,
    rowsPerPage: API_FETCH_LIMIT,
    profileType,
  });

  // Avoid forcing refetch on mount/tab switch. React Query will fetch when
  // queryKey params change or when the user explicitly triggers Apply.

  useEffect(() => {
    if (error) {
      setToast({
        message: error?.message,
        severity: "error",
      });

      setData([]);
      setChartData({ xAxis: [], yAxis: [], legend: "" });
    }
  }, [error]);

  useEffect(() => {
    if (apiData && apiData?.chartData) {
      setData(apiData?.transformedLiveData || []);
      setChartData(apiData?.chartData || { xAxis: [], yAxis: [], legend: "" });
    } else {
      setData([]);
      setChartData({ xAxis: [], yAxis: [], legend: "" });
    }
  }, [apiData]);

  const handleDateSubmit = () => {
    if (!startDate || !endDate) {
      setToast({
        message: "Please select both start and end dates",
        severity: "error",
      });
      return;
    }

    const startTs = getTimeStamp(formatTime(timeRange[0]), new Date(startDate));
    const endTs = getTimeStamp(formatTime(timeRange[1]), new Date(endDate));

    setTimeStamp({
      start: startTs,
      end: endTs,
    });

    setAnchorEl(null);

    setTimeout(() => {
      refetch();
    }, 500);
  };

  const downloadCSV = async () => {
    if (meterId) {
      try {
        setCsvLoader(true);
        const fromDate = moment
          .tz(timeStamp.start, "Asia/Kolkata")
          .startOf("day")
          .add(timeRange[0], "minutes")
          .format("YYYY-MM-DDTHH:mm");
        const toDate = moment
          .tz(timeStamp.end, "Asia/Kolkata")
          .startOf("day")
          .add(timeRange[1], "minutes")
          .format("YYYY-MM-DDTHH:mm");

        let v2Type = "instant";
        if (profileType === "Load Profile") v2Type = "load";
        else if (profileType === "Daily") v2Type = "daily";
        else if (profileType === "Billing") v2Type = "billing";

        const response = await fetchGETRequest<any>(
          `v2/meter-reads/browse?type=${v2Type}&skip=0&take=100000&search=${meterId}&from=${fromDate}&to=${toDate}`,
        );

        const records =
          response?.data?.records ||
          (Array.isArray(response?.data) ? response?.data : []);
        //  const meterData = await fetchGETRequest<any>(`v2/Meters/${meterId}`);

        if (records.length > 0) {
          const currentOptions = PROFILE_OPTIONS[profileType] || [];
          const tableData = records.map((record: any) => {
            const rowData: any = {
              meter: meterId,
              receivedDate: formatDate(
                findValue(record, "receivedDate"),
                "DD/MM/YYYY HH:mm:ss",
              ),
              timestamp: "",
            };

            currentOptions.forEach((option) => {
              const { val, readingTime } = transformV2Record(
                record,
                option.value,
              );
              if (!rowData.timestamp) {
                rowData.timestamp = formatDate(
                  readingTime,
                  "DD/MM/YYYY HH:mm:ss",
                );
              }
              rowData[option.type] = val ?? "-";
            });

            rowData.currentReadQuality = "Good";
            return rowData;
          });

          const headers = Object.keys(tableData[0]).join(",");
          const rows = tableData
            .map((row: any) => Object.values(row).join(","))
            .join("\n");

          const csvContent = `${headers}\n${rows}`;

          const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
          });

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `${v2Type}-${meterId}.csv`;
          link.click();

          URL.revokeObjectURL(link.href);
        } else {
          setToast({
            message: `No data found for meter: ${meterId}`,
            severity: "error",
          });
        }
      } catch (error) {
        return error;
      } finally {
        setCsvLoader(false);
        setDownloadEl(null);
      }
    } else {
      setToast({
        message: "Please enter meter Id",
        severity: "error",
      });
    }
  };

  const downloadExcel = async () => {
    try {
      if (meterId) {
        setExcelLoader(true);
        const fromDate = moment
          .tz(timeStamp.start, "Asia/Kolkata")
          .startOf("day")
          .add(timeRange[0], "minutes")
          .format("YYYY-MM-DDTHH:mm");
        const toDate = moment
          .tz(timeStamp.end, "Asia/Kolkata")
          .startOf("day")
          .add(timeRange[1], "minutes")
          .format("YYYY-MM-DDTHH:mm");

        let v2Type = "instant";
        if (profileType === "Load Profile") v2Type = "load";
        else if (profileType === "Daily") v2Type = "daily";
        else if (profileType === "Billing") v2Type = "billing";

        const response = await fetchGETRequest<any>(
          `v2/meter-reads/browse?type=${v2Type}&skip=0&take=10000&search=${meterId}&from=${fromDate}&to=${toDate}`,
        );

        const records =
          response?.data?.records ||
          (Array.isArray(response?.data) ? response?.data : []);

        if (records.length === 0) {
          setToast({
            message: `No data found.`,
            severity: "error",
          });
          return;
        }

        const currentOptions = PROFILE_OPTIONS[profileType] || [];
        const transformedLiveData = records.map((record: any) => {
          const rowData: any = {
            receivedDate: formatDate(
              findValue(record, "receivedDate"),
              "DD/MM/YYYY HH:mm:ss",
            ),
            currentDateTime: "",
          };

          currentOptions.forEach((option) => {
            const { val, readingTime } = transformV2Record(
              record,
              option.value,
            );
            if (!rowData.currentDateTime) {
              rowData.currentDateTime = formatDate(
                readingTime,
                "DD/MM/YYYY HH:mm:ss",
              );
            }
            rowData[option.type] = val ?? "-";
          });

          rowData.currentReadQuality = "Good";
          return rowData;
        });

        const headers = Object.keys(transformedLiveData[0]).join("\t");
        const rows = transformedLiveData
          .map((row: any) => Object.values(row).join("\t"))
          .join("\n");
        const excelContent = `${headers}\n${rows}`;

        const blob = new Blob([excelContent], {
          type: "application/vnd.ms-excel;charset=utf-8;",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${v2Type}-${meterId}.xls`;
        link.click();

        URL.revokeObjectURL(link.href);
      } else {
        setToast({
          message: "Please enter meter Id",
          severity: "error",
        });
      }
    } catch (error) {
      return error;
    } finally {
      setExcelLoader(false);
      setDownloadEl(null);
    }
  };

  const downloadPrevDayBlockCSV = async () => {
    if (meterId) {
      try {
        setCsvLoader(true);
        const fromDate = moment()
          .tz("Asia/Kolkata")
          .subtract(1, "days")
          .startOf("day")
          .add(10, "minutes")
          .format("YYYY-MM-DDTHH:mm");
        const toDate = moment()
          .tz("Asia/Kolkata")
          .startOf("day")
          .format("YYYY-MM-DDTHH:mm");

        const v2Type = "load"; // Block Reading

        const response = await fetchGETRequest<any>(
          `v2/meter-reads/browse?type=${v2Type}&skip=0&take=100000&search=${meterId}&from=${fromDate}&to=${toDate}`,
        );

        const records =
          response?.data?.records ||
          (Array.isArray(response?.data) ? response?.data : []);

        if (records.length > 0) {
          const currentOptions = PROFILE_OPTIONS["Load Profile"] || [];
          const tableData = records.map((record: any) => {
            const rowData: any = {
              meter: meterId,
              receivedDate: formatDate(
                findValue(record, "receivedDate"),
                "DD/MM/YYYY HH:mm:ss",
              ),
              timestamp: "",
            };

            currentOptions.forEach((option) => {
              const { val, readingTime } = transformV2Record(
                record,
                option.value,
              );
              if (!rowData.timestamp) {
                rowData.timestamp = formatDate(
                  readingTime,
                  "DD/MM/YYYY HH:mm:ss",
                );
              }
              rowData[option.type] = val ?? "-";
            });

            rowData.currentReadQuality = "Good";
            return rowData;
          });

          const headers = Object.keys(tableData[0]).join(",");
          const rows = tableData
            .map((row: any) => Object.values(row).join(","))
            .join("\n");

          const csvContent = `${headers}\n${rows}`;

          const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
          });

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `${v2Type}-${meterId}-prev-day.csv`;
          link.click();

          URL.revokeObjectURL(link.href);
        } else {
          setToast({
            message: `No data found for meter: ${meterId} for the previous day.`,
            severity: "error",
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setCsvLoader(false);
      }
    }
  };


  const getStatusChip = (type: string) => {
    return (
      <Box
        sx={{
          letterSpacing: "0.32px",
          padding: "2px 8px",
          borderRadius: "50px",
          width: "fit-content",
          background:
            type === "Failed kWh threshold validation"
              ? "#F65B5B"
              : CHIP_COLORS[type.toLowerCase() as keyof typeof CHIP_COLORS],
        }}
      >
        <Typography
          variant="tableBody"
          fontWeight={600}
          fontSize={"12px"}
          color="#ffffff"
        >
          {type}
        </Typography>
      </Box>
    );
  };

  // Client-side pagination: slice data based on current page
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return data.slice(startIndex, endIndex);
  }, [data, page, rowsPerPage]);

  const rowsContent = paginatedData?.map((item, index: number) => (
    <TableRow key={index}>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {formatDate(
            item.readingTime || item.ts || item.currentDateTime,
            "DD/MM/YYYY HH:mm:ss",
          ) || item.currentDateTime}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {typeof item.currentRead === "boolean"
            ? item.currentRead
              ? "Open"
              : "Close"
            : item.currentRead === "true"
              ? "Open"
              : item.currentRead === "false"
                ? "Close"
                : isNaN(Number(item.currentRead))
                  ? item.currentRead
                  : Number(item.currentRead).toFixed(4)}
        </Typography>
      </TableCell>
      <TableCell>{getStatusChip(item.currentReadQuality)}</TableCell>
      <TableCell>
        <IconButton onClick={() => setUpdateReadValueModal(item)}>
          {/* <UnifedIcon
            icon={Edit2}
            fontSize={8}
            style={{ color: "#000000", fontSize: "12px" }}
          /> */}
          <Edit2 color="#000000" className="!size-4" />
        </IconButton>
      </TableCell>
    </TableRow>
  ));

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start ? moment.tz(start, "Asia/Kolkata").toDate() : null);
    setEndDate(end ? moment.tz(end, "Asia/Kolkata").toDate() : null);
  };

  return (
    <Box>
      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />
      <Stack px={2}>
        <Box
          sx={{
            mt: 3,
            p: "12px",
            borderRadius: "10px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Stack
            direction={"row"}
            gap={2}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Stack direction={"row"} gap={2} alignItems={"center"}>
              <CustomInputLabelSelect
                value={
                  PROFILE_TYPES.find((p) => p.value === profileType) ||
                  PROFILE_TYPES[0]
                }
                onChange={(e) => {
                  const newProfile = String(e.value);
                  setProfileType(newProfile);
                  const newOptions = PROFILE_OPTIONS[newProfile] || [];
                  if (newOptions.length > 0) {
                    setRegisterType(newOptions[0]);
                  }
                }}
                icon={DownArrow}
                title={"Read Type"}
                options={PROFILE_TYPES}
                titleSx={{
                  fontSize: "14px",
                  lineHeight: "20px",
                  whiteSpace: "nowrap",
                }}
                iconSx={{
                  marginRight: "5px",
                  marginTop: "5px",
                }}
              />
              <CustomInputLabelSelect
                value={registerType}
                onChange={(e) =>
                  setRegisterType({
                    type: e.type,
                    value: String(e.value),
                  })
                }
                icon={DownArrow}
                title={"Register Type"}
                options={PROFILE_OPTIONS[profileType] || []}
                titleSx={{
                  fontSize: "14px",
                  lineHeight: "20px",
                  whiteSpace: "nowrap",
                }}
                iconSx={{
                  marginRight: "5px",
                  marginTop: "5px",
                }}
              />
            </Stack>

            <Stack flexDirection={"row"} alignItems={"center"}>
              <Box
                sx={{
                  fontWeight: 600,
                  letterSpacing: "0.32px",
                  padding: "1px 8px",
                  color: "#ffffff",
                  width: "fit-content",
                }}
              >
                <Stack
                  direction={"row"}
                  justifyContent={"center"}
                  gap={"5px"}
                  alignItems={"center"}
                  px={"5px"}
                >
                  <Typography fontSize={"13px"} fontWeight={500}>
                    {formatDate(startDate, "D MMMM YYYY")} -{" "}
                  </Typography>
                  <Typography fontSize={"13px"} fontWeight={500}>
                    {formatDate(endDate, "D MMMM YYYY")}
                  </Typography>
                </Stack>
              </Box>

              <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
                <Calendar2 />
              </IconButton>
              <Box
                sx={{
                  width: "1px",
                  height: "31px",
                  background: "#CACDD7",
                  margin: "0px 20px",
                }}
              ></Box>

              {/* <CustomInputLabelSelect
                value={
                  PROFILE_TYPES.find((p) => p.value === profileType) ||
                  PROFILE_TYPES[0]
                }
                onChange={(e) => {
                  const newProfile = String(e.value);
                  setProfileType(newProfile);
                  const newOptions = PROFILE_OPTIONS[newProfile] || [];

                  // Try to maintain existing register type or match closely
                  const currentTypeStr = registerType.type;
                  const match =
                    newOptions.find((opt) => opt.type === currentTypeStr) ||
                    newOptions.find(
                      (opt) =>
                        opt.type.startsWith(currentTypeStr) ||
                        currentTypeStr.startsWith(opt.type),
                    );

                  if (match) {
                    setRegisterType(match);
                  } else if (newOptions.length > 0) {
                    setRegisterType(newOptions[0]);
                  }
                }}
                icon={DownArrow}
                title={"Profile"}
                options={PROFILE_TYPES}
                titleSx={{
                  fontSize: "14px",
                  lineHeight: "20px",
                  whiteSpace: "nowrap",
                }}
                iconSx={{
                  marginRight: "5px",
                  marginTop: "5px",
                }}
              /> */}

              <ContainedButton
                disabled={!startDate || !endDate || !registerType}
                label={"Apply"}
                sx={{
                  color: "#ffffff",
                  width: "100px",
                  boxShadow: "0",
                  marginLeft: "10px",
                }}
                onClick={refetch}
                loading={isLoading}
              />
              <Stack direction={"row"} gap={"20px"} ml={"10px"}>
                <IconButton
                  onClick={(event) => setDownloadEl(event.currentTarget)}
                >
                  <Download />
                </IconButton>

                <Popover
                  open={!!downloadEl}
                  anchorEl={downloadEl}
                  onClose={() => setDownloadEl(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  {csvLoader ? (
                    <Stack sx={{ p: 2 }}>
                      <LottieLoader />
                    </Stack>
                  ) : (
                    <IconButton onClick={downloadCSV}>
                      <Typography sx={{ p: 2 }}>Csv</Typography>
                    </IconButton>
                  )}
                  <Divider />
                  {excelLoader ? (
                    <Stack sx={{ p: 2 }}>
                      <LottieLoader />
                    </Stack>
                  ) : (
                    <IconButton onClick={downloadExcel}>
                      <Typography sx={{ p: 2 }}>Excel</Typography>
                    </IconButton>
                  )}
                </Popover>
              </Stack>
              <Tooltip title="One-click download for previous day's block readings (12:10 AM - 12:00 AM)">
                <Box sx={{ ml: "10px" }}>
                  <ContainedButton
                    onClick={downloadPrevDayBlockCSV}
                    loading={csvLoader}
                    label="Prev Day Block CSV"
                    bgColor="#10B981"
                    hoverBgColor="#059669"
                    sx={{
                      fontSize: "12px",
                      height: "35px",
                      whiteSpace: "nowrap",
                      boxShadow: "none",
                    }}
                  />
                </Box>
              </Tooltip>
            </Stack>
          </Stack>

          <Card
            sx={{
              p: 0,
              height: `${
                chartData?.xAxis?.length === 0 && chartData?.yAxis?.length === 0
                  ? "203px"
                  : "400px"
              }`,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "10px",
            }}
          >
            {chartData?.xAxis?.length === 0 &&
            chartData?.yAxis?.length === 0 ? (
              <Typography variant="h6">No chart found</Typography>
            ) : (
              <ReadClusterLineChart
                xAxisData={chartData.xAxis}
                yAxisData={chartData?.yAxis}
                legend={chartData?.legend || ""}
              />
            )}
          </Card>
        </Box>

        <Card
          sx={{
            maxHeight: "640px",
            minHeight: "203px",
            mt: 2,
            p: "12px 12px 0px 12px",
          }}
        >
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={"20px"}
          >
            <Typography variant="h6" fontSize={"14px"} fontWeight={600}>
              Reading Records
            </Typography>
            <ContainedButton
              label={"Upload Reads"}
              sx={{
                color: "#ffffff",
                fontSize: "12px",
                fontWeight: 500,
                borderRadius: "8px",
              }}
              onClick={() => setCreateReadValueModal(true)}
            />
          </Stack>
          <CustomDataTable
            sx={{
              maxHeight: "600px",
              mt: 2,
            }}
            columns={COLUMNS}
            rowsContent={rowsContent}
            totalCount={data?.length || 0}
            isLoading={isLoading}
            pagination={data?.length > 0}
            Actions={true}
            rowsPerPage={rowsPerPage}
            page={page}
            rowsPerPageOptions={[10, 50, 100, 250, 500]}
            onPageChange={(e, num) => setPage(num)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0); // Reset to first page when changing rows per page
            }}
          />
        </Card>
      </Stack>
      <DateAndTimePopover
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        startDate={startDate as Date}
        endDate={endDate as Date}
        handleDateChange={handleDateChange}
        timeRange={timeRange}
        setTimeRange={(timeRange) =>
          setTimeRange(
            Array.isArray(timeRange) ? timeRange : [timeRange, timeRange],
          )
        }
        handleDateSubmit={handleDateSubmit}
      />

      {meterId && updateReadValueModal && updateReadValueModal && (
        <UpdateReadValueModel
          onClose={() => setUpdateReadValueModal(null)}
          readValue={updateReadValueModal}
          meterId={meterId}
          onSuccess={() => {
            setUpdateReadValueModal(null);
            refetch();
          }}
        />
      )}

      {createReadValueModal && meterId && (
        <CreateReadValueModel
          onClose={() => setCreateReadValueModal(false)}
          meterId={meterId}
          onSuccess={() => {
            setCreateReadValueModal(false);
            refetch();
          }}
          setToast={setToast}
        />
      )}
    </Box>
  );
};
